const { debounce, getElement, getElements } = window.BAO.Utils

class CollectionFiltersRenderer {
  /**
   * @param {CollectionFiltersForm} form
   */
  constructor (form) {
    /**
     * @type {CollectionFiltersForm}
     * @public
     */
    this.form = form

    /**
     * @type {CollectionFiltersPaginationRender}
     * @public
     */
    this.paginationRenderer = new CollectionFiltersPaginationRender(this)
  }

  /**
   * @param {Function} filterDataUrl
   * @param {SubmitEvent} e
   *
   * @returns {void}
   */
  fromCache (filterDataUrl, e) {
    const html = this.form.filterData.find(element => filterDataUrl(element))
      .html

    this.render(html, e)
  }

  /**
   * @param {String} section
   * @param {String} url
   * @param {SubmitEvent} e
   *
   * @returns {void}
   */
  async fromFetch (section, url, e) {
    const html = await window.BAO.sectionRenderer.fetchSingle(section, url)

    this.form.filterData.push({
      html,
      url,
    })

    this.render(html, e)
  }

  /**
   * @param {String} html
   * @param {SubmitEvent} e
   *
   * @returns {void}
   */
  render (html, e) {
    this.renderFilters(html, e)
    this.renderProductGridContainer(html, e)
    this.renderProductCount(html)
    this.renderSortLabel(html)
    this.paginationRenderer.render(html)

    this.form.loading = false
  }

  /**
   * @param {String} html
   * @param {SubmitEvent} e
   *
   * @returns {void}
   */
  renderFilters (html, e) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')
    const detailSelector = [
      this.form.els?.facet?.selector,
      this.form.externalEls?.facet?.selector,
    ]
      .filter(Boolean)
      .join(',')

    if (!detailSelector) return

    const facetDetailElements = getElements(detailSelector, {
      context: parsedHTML,
    })
    /**
     * @param {HTMLElement} element
     *
     * @returns {Boolean}
     */
    const matchesIndex = element => {
      const facet = e ? e.target.closest(detailSelector) : null

      return facet ? element.dataset.index === facet.dataset.index : false
    }

    const facetsToRender = facetDetailElements.filter(el => !matchesIndex(el))

    facetsToRender.forEach(element => {
      const selector = detailSelector
        .split(',')
        .map(selector => `${selector}[data-index="${element.dataset.index}"]`)
        .join(',')

      const el = getElement(selector)

      if (!el.classList.contains('clc-ProductGridFacet-price')) {
        el.innerHTML = element.innerHTML
      }
    })

    this.renderFacetCount(parsedHTML)
    this.renderMobileFacetSummary(parsedHTML)
    this.renderAdditionalElements(parsedHTML)
    this.renderActiveFacets(parsedHTML)
  }

  renderFacetCount (parsedHTML) {
    const facetCountEl = parsedHTML.querySelector(
      this.form?.els?.['facet-count']?.selector,
    )

    if (!facetCountEl || !this.form?.els?.['facet-count']?.element) return

    this.form.els['facet-count'].element.textContent = facetCountEl.textContent
  }

  /**
   * @param {Document} parsedHTML
   *
   * @returns {void}
   */
  renderActiveFacets (parsedHTML) {
    this.activeFacetSelectors.forEach(selector => {
      const activeFacetElement = parsedHTML.querySelector(selector)

      if (!activeFacetElement) return

      getElements(selector).forEach(
        el => (el.innerHTML = activeFacetElement.innerHTML),
      )
    })

    this.updateActiveFilterCount()
    this.renderResetLink(parsedHTML)
  }

  updateActiveFilterCount () {
    const facets = document.querySelectorAll('.clc-ProductGridFacet')

    facets.forEach(facet => {
      const selectedFacets = facet.querySelectorAll(
        'input[type="checkbox"]:checked',
      )
      const count = selectedFacets.length

      const countElement = facet.querySelector('[data-active-filter-count')

      if (countElement && count === 0) {
        countElement.textContent = ` `
      } else if (countElement && count > 0) {
        countElement.textContent = ` (${count})`
      }
    })
  }

  /**
   * @param {Document} parsedHTML
   *
   * @returns {void}
   */
  renderMobileFacetSummary (parsedHTML) {
    getElements(this.form.els['facet-summary'].selector).forEach(summaryEl => {
      const matchedSummaryElement = parsedHTML.querySelector(
        `${this.form.els['facet-summary'].selector}[data-handle='${summaryEl.dataset.handle}']`,
      )

      if (!matchedSummaryElement) return

      summaryEl.innerHTML = matchedSummaryElement.innerHTML
    })
  }

  /**
   * @param {Document} parsedHTML
   *
   * @returns {void}
   */
  renderAdditionalElements (parsedHTML) {}

  /**
   * @param {String} html
   * @param {PointerEvent} e
   *
   * @returns {void}
   */
  renderProductGridContainer (html, e) {
    const direction = this.getPaginationDirection(e)
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')

    const newItems = document.createDocumentFragment()
    const newElements = getElements(this.form.externalEls.item.selector, {
      context: parsedHTML,
    })

    newElements.forEach(el => newItems.appendChild(el))

    if (direction == null) {
      this.form.externalEls['item-container'].element.innerHTML = ''
      this.form.externalEls['item-container'].element.append(newItems)
    } else if (direction === 'PREVIOUS') {
      this.form.externalEls['item-container'].element.prepend(newItems)
    } else if (direction === 'NEXT') {
      this.form.externalEls['item-container'].element.append(newItems)
    }

    document
      .querySelectorAll(
        '[data-collection-filters-form-external-el="item"] img[srcset]',
      )
      .forEach(image => {
        // eslint-disable-next-line no-self-assign
        image.outerHTML = image.outerHTML
      })
  }

  /**
   * @param {String} html
   *
   * @returns {void}
   */
  renderProductCount (html) {
    const targetEl = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('[data-product-grid-count-data]')

    if (!targetEl) {
      return
    }

    const countElements = getElements('[data-product-grid-count]')

    try {
      const { displayOne, displayOther, count } = targetEl.dataset

      const products = document.querySelectorAll(
        '[data-collection-filters-form-external-el="item"]',
      )

      const productCount = products.length

      function replace (str, productCount, count) {
        return str
          .replace('{{ product_count }}', productCount)
          .replace('{{ count }}', count)
      }

      if (productCount > 0) {
        const template = productCount === 1 ? displayOne : displayOther
        countElements.forEach(
          element =>
            (element.innerHTML = replace(template, productCount, count)),
        )
      } else {
        countElements.forEach(element => (element.innerHTML = ''))
      }

      // Hide if the load more button is not present
      const footerCount = this.form?.externalEls['footer-count'].element

      if (footerCount) {
        footerCount.setAttribute(
          'aria-hidden',
          Number(productCount) === Number(count) ? 'true' : 'false',
        )
      }
    } catch (err) {
      countElements.forEach(element => (element.innerHTML = ''))
    }
  }

  /**
   * @param {String} html
   *
   * @returns {void}
   */
  renderSortLabel (html) {
    const label = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(this.form?.els['sort-label']?.selector)?.innerHTML

    if (!label) return

    this.form.els['sort-label'].elements.forEach(
      element => (element.innerHTML = label),
    )
  }

  /**
   * @param {String} html
   *
   * @returns {void}
   */
  renderResetLink (html) {
    const resetLink = html.querySelector(this.form?.els['reset-link']?.selector)

    if (!resetLink) return

    this.form.els['reset-link'].element.innerHTML = resetLink.innerHTML
  }

  /**
   * @param {HTMLElement} source
   * @param {HTMLElement} target
   *
   * @returns {void}
   */
  renderSelectedFacetCount (source, target) {
    const sourceElement = getElement(
      this.form.els['facets-selected']?.selector,
      { context: source },
    )
    const targetElement = getElement(
      this.form.els['facets-selected']?.selector,
      { context: target },
    )

    if (sourceElement && targetElement) {
      targetElement.outerHTML = sourceElement.outerHTML
    }
  }

  /**
   * @param {PointerEvent} e
   *
   * @return 'NEXT'|'PREVIOUS'|null
   */
  getPaginationDirection (e) {
    if (typeof e === 'undefined') return null

    const paginationElement = e.target.closest(
      `${this.form.externalEls['load-previous'].selector}, ${this.form.externalEls['load-more'].selector}`,
    )
    if (!paginationElement) return null

    return paginationElement === this.form.externalEls['load-previous']?.element
      ? 'PREVIOUS'
      : 'NEXT'
  }

  /**
   * @returns {String[]}
   */
  get activeFacetSelectors () {
    if ('active-facets' in this.form.externalEls) {
      return [
        this.form.els['active-facets']?.selector,
        this.form.externalEls['active-facets']?.selector,
      ]
    }

    return [this.form.els['active-facets']?.selector]
  }
}

class CollectionFiltersPaginationRender {
  constructor (renderer) {
    /**
     * @type {CollectionFiltersRenderer}
     * @public
     */
    this.renderer = renderer
  }

  /**
   * @param {string} html
   */
  render (html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')

    this.renderLoadPrevious(parsedHTML)
    this.renderLoadMore(parsedHTML)
  }

  /**
   * @param {Document} html
   */
  renderLoadPrevious (html) {
    if (!this.shouldShowLoadPrevious) {
      return (this.form.externalEls['load-previous'].element.innerHTML = '')
    }

    if (!this.shouldRerenderLoadPrevious) return

    return this._renderEl('load-previous', html)
  }

  /**
   * @param {Document} html
   */
  renderLoadMore (html) {
    if (!this.shouldShowLoadMore) {
      return (this.form.externalEls['load-more'].element.innerHTML = '')
    }

    if (!this.shouldRerenderLoadMore) return

    return this._renderEl('load-more', html)
  }

  /**
   * @param {string} section
   * @param {Document} html
   */
  _renderEl (section, html) {
    this.form.externalEls[section].element.innerHTML = getElement(
      this.form.externalEls[section].selector,
      { context: html },
    ).innerHTML
  }

  get form () {
    return this.renderer.form
  }

  get shouldShowLoadPrevious () {
    const anElementFromTheFirstPageExists = !!getElement(
      `${this.form.externalEls.item.selector}[data-page="1"]`,
    )
    if (anElementFromTheFirstPageExists) return false

    return this.currentPage > 1
  }

  get shouldShowLoadMore () {
    const anElementFromTheLastPageExists = getElement(
      `${this.form.externalEls.item.selector}[data-page="${this.lastPage}"]`,
    )
    if (anElementFromTheLastPageExists) return false

    return Number(this.currentPage) < this.lastPage
  }

  get shouldRerenderLoadPrevious () {
    return this.currentPage <= this.currentLowestPage
  }

  get shouldRerenderLoadMore () {
    return this.currentPage >= this.currentHighestPage
  }

  get currentPage () {
    const params = this.form.searchParams.current
    const page = params.get('page')

    return Number(page) || 1
  }

  get currentLowestPage () {
    const productGridItems = getElements(
      `${this.form.externalEls.item.selector}`,
    )

    return Number(productGridItems[0].dataset.page) || 1
  }

  get currentHighestPage () {
    const productGridItems = getElements(
      `${this.form.externalEls.item.selector}`,
    )

    return (
      Number(productGridItems[productGridItems.length - 1].dataset.page) || 1
    )
  }

  /**
   * @returns {number}
   */
  get lastPage () {
    return Number(
      this.form.externalEls['product-grid'].element.dataset.lastPage,
    )
  }
}

/**
 * @typedef {Object} FilterData
 *
 * @property {string} html
 * @property {string} url
 */

export class CollectionFiltersForm extends window.BAO.CustomElement({
  SuperClass: HTMLFormElement,
}) {
  static get requiredElements () {
    return ['facets', 'facet', 'facet-summary']
  }

  static get requiredExternalElements () {
    return ['container', 'product-grid']
  }

  static isPaginating = false

  constructor () {
    super()

    /**
     * @type {Section[]}
     * @public
     */
    this.sections = [
      {
        id: 'template-collection-product-grid',
        section: this.dataset.id,
        selector: '.shopify-section',
      },
    ]

    /**
     * @type {FilterData[]}
     * @public
     */
    this.filterData = []

    /**
     * @type {{ previous: URLSearchParams, current: URLSearchParams, initial: URLSearchParams }}
     * @public
     */
    this.searchParams = {
      // eslint-disable-next-line compat/compat
      previous: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      current: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      initial: new URLSearchParams(window.location.search),
    }

    /**
     * @type {CollectionFiltersRenderer}
     * @public
     */
    this.renderer = new CollectionFiltersRenderer(this)
    this.renderer.updateActiveFilterCount()
  }

  setupListeners () {
    super.setupListeners()

    this.onFacetRemovalClick = this.onFacetRemovalClick.bind(this)
    this.debouncedOnSubmit = debounce(e => this.onSubmit(e), 500)
    this.debouncedOnSubmit = this.debouncedOnSubmit.bind(this)

    this.listeners.add(this, 'input', event => {
      if (event.target.getAttribute('data-facet-search-el') !== 'search') {
        this.debouncedOnSubmit(event)
      }
    })

    this.listeners.add(window, 'popstate', this.onHistoryChange.bind(this))
    this.listeners.add(this.els.facets.element, 'keyup', event => {
      if (event.target.getAttribute('data-facet-search-el') !== 'search') {
        this.onKeyUp.bind(this)
      }
    })

    this.listeners.add(document, 'click', this.onPaginationClick.bind(this))
    this.listeners.add(document, 'click', this.onLinkClick.bind(this))
    this.listeners.add(document, 'click', this.onExternalFilterClick.bind(this))

    if (window.location.hash) {
      this.repositionToElement()
    }
  }

  /**
   * @param {PopStateEvent} e
   *
   * @returns {void}
   */
  onHistoryChange (e) {
    const searchParams =
      e && e.state
        ? e.state.searchParams
        : this.form && this.form.searchParams
          ? this.form.searchParams.initial
          : null

    if (
      this.form &&
      this.form.searchParams &&
      this.form.searchParams.previous &&
      searchParams &&
      searchParams.toString() === this.form.searchParams.previous.toString()
    ) {
      return
    }

    if (this.form) {
      this.form.renderPage({
        searchParams,
        updateURLHash: false,
      })
    }
  }

  /**
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  onFacetRemovalClick (e) {
    e.preventDefault()

    const searchParams = e.currentTarget.href.includes('?')
      ? e.currentTarget.href.slice(e.currentTarget.href.indexOf('?') + 1)
      : ''

    this.renderPage({ searchParams })
  }

  /**
   * @param {KeyboardEvent} e
   *
   * @returns {void}
   */
  onKeyUp (e) {
    if (e.code.toUpperCase() !== 'ESCAPE') return

    const openFacet = e.target.closest(`${this.els.facet.selector}[open]`)
    if (!openFacet) return

    const summary = getElement(`${this.els['facet-summary'].selector}`, {
      context: openFacet,
    })
    openFacet.removeAttribute('open')
    summary.setAttribute('aria-expanded', 'false')
    summary?.focus()
  }

  /**
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  onPaginationClick (e) {
    if (!e.target.closest(this.externalEls?.pagination?.selector)) return
    if (this.constructor.isPaginating) return

    e.preventDefault()

    const anchor = e.target.closest('a')

    if (!anchor || anchor.getAttribute('href') === '' || anchor.disabled) return

    this.constructor.isPaginating = true

    const href = anchor.getAttribute('href')

    // eslint-disable-next-line compat/compat
    const paginationSearchParams = new URLSearchParams(href.split('?')[1])
    const wantedPage = paginationSearchParams.get('page') ?? '1'
    // eslint-disable-next-line compat/compat
    const currentSearchParams = new URLSearchParams(
      this.searchParams.current.toString(),
    )
    currentSearchParams.set('page', wantedPage)

    this.renderPage({
      searchParams: currentSearchParams.toString(),
      e,
    })
  }

  /**
   * @param {PointerEvent} e
   */
  onLinkClick (e) {
    if (!e.target.closest('a')) return

    const linkHasPageReference = e.target.closest(`[data-page]`)
    if (!linkHasPageReference) return true

    e.preventDefault()

    this.searchParams.current.set('page', linkHasPageReference.dataset.page)

    let newUrl = `${window.location.pathname}${this.searchParams.current &&
      '?'.concat(this.searchParams.current.toString())}`

    if (linkHasPageReference.hasAttribute('id')) {
      newUrl = `${newUrl}#${linkHasPageReference.getAttribute('id')}`
    }

    history.pushState(
      { searchParams: this.searchParams.current.toString() },
      '',
      newUrl,
    )

    window.location.href = e.target.getAttribute('href')
  }

  /**
   * @param {PointerEvent} e
   */
  onExternalFilterClick (e) {
    if (!e.target.closest(this.externalEls['external-filter']?.selector)) return

    const externalFilterEl = e.target.closest(
      this.externalEls['external-filter'].selector,
    )
    const filterCategory = externalFilterEl.dataset.filterCategory
    const filterValue = externalFilterEl.dataset.filterValue

    if (filterValue.includes('*')) {
      const targetFilters = getElements(
        `[name="${filterCategory}"][value*="${filterValue.replace('*', '')}"]`,
        {
          context: this,
        },
      )

      targetFilters.forEach(el => {
        el.checked = !el.checked
        el.dispatchEvent(new Event('input', { bubbles: true }))
      })

      return
    }

    const targetFilter = getElement(
      `[name="${filterCategory}"][value="${filterValue}"]`,
      {
        context: this,
      },
    )

    if (!targetFilter) return
    targetFilter.checked = !targetFilter.checked
    targetFilter.dispatchEvent(new Event('input', { bubbles: true }))
  }

  repositionToElement () {
    const callback = () => {
      const el = getElement(window.location.hash)
      if (!el) return

      const { top } = el.getBoundingClientRect()
      const headerHeight = getElement(`#header`)?.offsetHeight || 0
      const offset = 20

      window.setTimeout(() => {
        window.scrollTo(0, window.scrollY + (top - headerHeight - offset))
      }, 100)
    }

    if (document.readyState === 'complete') {
      callback()
    } else {
      document.addEventListener('readystatechange', callback)
    }
  }

  /**
   * @param {InputEvent} e
   *
   * @returns {void}
   */
  onSubmit (e) {
    e.preventDefault()

    const formData = new FormData(this)
    // eslint-disable-next-line compat/compat
    const searchParams = new URLSearchParams(formData).toString()

    this.renderPage({
      searchParams,
      e,
    })
  }

  /**
   * @param {Object}
   * @param {String} searchParams
   * @param {InputEvent} [e]
   * @param {Boolean} [updateURLHash]
   *
   * @returns {void}
   */
  renderPage ({ searchParams, e, updateURLHash = true } = {}) {
    this.searchParams.previous = this.searchParams.current
    // eslint-disable-next-line compat/compat
    this.searchParams.current = new URLSearchParams(searchParams)

    this.loading = true

    this.sections.forEach(section => {
      const url = `${window.location.pathname}?section_id=${
        section.section
      }&${this.searchParams.current.toString()}`
      /**
       * @param {FilterData} element
       *
       * @returns {Boolean}
       */
      const filterDataUrl = element => element.url === url

      this.filterData.some(element => filterDataUrl(element))
        ? this.renderer.fromCache(filterDataUrl, e)
        : this.renderer.fromFetch(section.section, url, e)
    })

    if (updateURLHash) this.updateURLHash(this.searchParams.current)
  }

  /**
   * @param {URLSearchParams} searchParams
   *
   * @returns {void}
   */
  updateURLHash (searchParams) {
    history.pushState(
      { searchParams: searchParams.toString() },
      '',
      `${window.location.pathname}${searchParams &&
        '?'.concat(searchParams.toString())}`,
    )
  }

  /**
   * @returns {Boolean}
   */
  get loading () {
    return this._loading
  }

  /**
   * @param {Boolean} val
   *
   * @returns {void}
   */
  set loading (val) {
    this._loading = val

    this.setAttribute('aria-busy', val.toString())

    this.externalEls?.container.element.setAttribute(
      'aria-busy',
      val.toString(),
    )

    if (val === false) {
      this.constructor.isPaginating = false
    }
  }
}

if (!customElements.get('collection-filters-form')) {
  customElements.define('collection-filters-form', CollectionFiltersForm, {
    extends: 'form',
  })
}

class PriceRange extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['min', 'max']
  }

  constructor () {
    super()

    this.setMinAndMaxValues()
  }

  setupListeners () {
    super.setupListeners()

    this.onRangeChange = this.onRangeChange.bind(this)

    getElements('input', { context: this }).forEach(el =>
      this.listeners.add(el, 'change', this.onRangeChange),
    )
  }

  /**
   * @param {InputEvent} e
   *
   * @returns {void}
   */
  onRangeChange (e) {
    this.adjustToValidValues(e.currentTarget)
    this.setMinAndMaxValues()
  }

  setMinAndMaxValues () {
    if (this.maxInput.value) {
      this.minInput.setAttribute('max', this.maxInput.value)
    }
    if (this.minInput.value) {
      this.maxInput.setAttribute('min', this.minInput.value)
    }
    if (this.minInput.value === '') this.maxInput.setAttribute('min', '0')
    if (this.maxInput.value === '') {
      this.minInput.setAttribute('max', this.maxInput.getAttribute('max'))
    }
  }

  /**
   * @param {HTMLInputElement} input
   *
   * @returns {void}
   */
  adjustToValidValues (input) {
    const value = Number(input.value)
    const min = Number(input.getAttribute('min'))
    const max = Number(input.getAttribute('max'))

    if (value < min) input.value = min.toString()
    if (value > max) input.value = max.toString()
  }

  /**
   * @returns {HTMLInputElement}
   */
  get maxInput () {
    return this.els.max.element
  }

  /**
   * @returns {HTMLInputElement}
   */
  get minInput () {
    return this.els.min.element
  }
}

if (!customElements.get('price-range')) {
  customElements.define('price-range', PriceRange)
}

class FacetRemove extends window.BAO.CustomElement() {
  setupListeners () {
    super.setupListeners()

    this.listeners.add(this.querySelector('a'), 'click', e => {
      e.preventDefault()

      this.resetRangeSlider()
      this.form.onFacetRemovalClick(e)

      const searchParams = new URL(e.currentTarget.href).searchParams.toString()

      this.form.renderPage({
        searchParams,
        updateURLHash: false,
        e,
      })
    })
  }

  resetRangeSlider () {
    const rangeSlider = document.querySelector('range-slider')

    if (!rangeSlider) return
    rangeSlider.reset()
  }

  get form () {
    const selector = 'collection-filters-form, [is="collection-filters-form"]'

    return this.closest(selector) || document.querySelector(selector)
  }
}

if (!customElements.get('facet-remove')) {
  customElements.define('facet-remove', FacetRemove)
}

class FilterFacet extends window.BAO.CustomElement({
  SuperClass: HTMLDetailsElement,
}) {
  static get requiredElements () {
    return ['summary', 'content']
  }

  constructor () {
    super()

    /**
     * @type {Animation|null}
     * @public
     */
    this.animation = null

    /**
     * @type {Boolean}
     * @public
     */
    this.isClosing = false

    /**
     * @type {Boolean}
     * @public
     */
    this.isExpanding = false
  }

  setupListeners () {
    super.setupListeners()

    this.onSummaryClick = this.onSummaryClick.bind(this)

    this.listeners.add(this, 'click', this.onSummaryClick)
  }

  /**
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  onSummaryClick (e) {
    if (!e.target.closest(this.els.summary.selector)) return

    e.preventDefault()

    if (this.isClosing || !this.open) {
      this.start()
    } else if (this.isExpanding || this.open) {
      this.close()
    }
  }

  start () {
    this.open = true

    window.requestAnimationFrame(() => this.expand())
  }

  close () {
    this.shrink()
  }

  shrink () {
    this.isClosing = true

    if (this.animation) {
      this.animation.cancel()
    }

    const height = `${this.content.offsetHeight}px`

    this.animation = this.content.animate(
      {
        height: [height, 0],
      },
      {
        duration: 300,
        easing: 'ease',
      },
    )

    this.onAnimationFinish(false)
  }

  expand () {
    this.isExpanding = true

    const height = `${this.content.offsetHeight}px`

    if (this.animation) {
      this.animation.cancel()
    }

    this.animation = this.content.animate(
      {
        height: [0, height],
      },
      {
        duration: 300,
        easing: 'ease',
      },
    )

    this.onAnimationFinish(true)
  }

  /**
   * @param {Boolean} open
   *
   * @returns {void}
   */
  onAnimationFinish (open) {
    this.open = open
    this.animation = null

    this.isClosing = false
    this.isExpanding = false
  }

  get content () {
    return window.BAO.Utils.getElement(this.els.content.selector, {
      context: this,
    })
  }
}

if (!customElements.get('filter-facet')) {
  customElements.define('filter-facet', FilterFacet, { extends: 'details' })
}

class FacetSearch extends window.BAO.CustomElement() {
  setupListeners () {
    super.setupListeners()

    this.listeners.add(this.els?.search?.element, 'input', evt =>
      this.initFilterSearchBar(evt),
    )
  }

  connectedCallback () {
    super.connectedCallback()

    this.type = this.dataset.type

    this.filterListItems = this.closest(
      this.externalEls?.content?.selector,
    ).querySelectorAll(this.externalEls?.item?.selector)
  }

  get isModel () {
    return this.dataset.type === 'model'
  }

  initFilterSearchBar (evt) {
    evt.preventDefault()

    const searchTerm = normalizeString(evt.target.value)

    this.filterListItems.forEach(item => {
      const text = normalizeString(item.dataset.filterValue)

      item.ariaHidden = !text.includes(searchTerm)
    })

    // Normalise func used for accent terms
    // such as: `Pokémon` - returns `Pokemon`
    function normalizeString (str) {
      return str
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
    }
  }
}

if (!customElements.get('facet-search')) {
  customElements.define('facet-search', FacetSearch)
}
